// XXX: HARD RULE -- WHAT IS IN CONTEXT.
// XXX: An author's only options are the items already in context for the current
// XXX: DC: for a PAGE that is exactly its provides-contexts; for a COMPONENT that
// XXX: is provides-contexts PLUS required-contexts. Nothing else is ever in
// XXX: context. Standard/ambient entities (currentUser, resumeStory,
// XXX: configuration, appUser, hostStandardContextRoots) are NOT implicitly
// XXX: available: they reach a DC only when the author explicitly provides them
// XXX: with entitySource "session". A PROVIDING picker on the session source may
// XXX: list them; a CONSUMING picker (URL variables, element bindings,
// XXX: required-context prompts) must NOT, ever.
// XXX: Offering a context the DC does not have lets an author save a binding that
// XXX: can never resolve, which renders empty at runtime with no clue why.
// XXX: Every consuming picker must share ONE filtered candidate builder --
// XXX: per-modal lists are how ambient entries leak back in.
// XXX: See docs/CONTEXT_AVAILABILITY.md.
// Pure option builders for ProvideContextModal: appName-aware entity lookups,
// pinned-mode type gating (mismatches disabled, not hidden). CJS (node suite).

// Relative, not the `%` alias: the plain-node unit suite requires this file
// directly and has no webpack alias resolution.
const AppPaletteFieldPolicy = require("./app-palette-field-policy");

const KEY_SOURCE_URI = "uri";
const KEY_SOURCE_CONSTANT = "constant";
const KEY_SOURCE_CONTEXT = "context";
const KEY_SOURCE_CONFIG = "config";
// UI-only source spelling for ambient contexts; persists as entitySource
// "standard" (see ProvidesContextEntry.build) — a regrouping, not a new shape.
const KEY_SOURCE_SESSION = "session";
const KEY_SOURCE_CONTEXT_TYPE = "contextType";
// Feed's iterated row as its own source; persists as a plain contextType bind
// (ProvideContextSourceFields.buildFormState), not a distinct entitySource.
const KEY_SOURCE_FEED_ITEM = "feedItem";
// The separator label a feed computes for the section this component renders.
// Like feedItem it is its OWN source rather than a row in the candidate list:
// it binds a VALUE the feed supplies per section, not an entity, so it carries
// no entityType and needs no instance picker.

const MSG_FIELD_WRONG_TYPE = "%field points at a %have, not a %want";
const MSG_WRONG_TYPE = "%source holds a %have, not a %want";

// Which entity field types can satisfy a PRIMITIVE required context of each
// value type. Keys are the engine's closed field-type enum (app.schema.json
// entityField.type). Deliberately excluded from every list:
//   oneEntity / manyEntities — relationships, not scalars; those feed
//     buildChainFieldOptions instead.
//   story        — rich-text JSON, not a scalar.
//   stringArray  — an array, not a scalar.
//   image / video — these hold a MEDIA ID, not a URL. The URL is resolved at
//     render time (DynamicAppImageElement resolves media_id → url via the
//     media-playback store), so binding one to a string context would surface
//     an opaque id rather than a usable string.
const VALUE_TYPE_FIELD_TYPES = {
  string: ["string", "email", "enum"],
  number: ["int", "float", "datetime", "denormalizedAggregate"],
  boolean: ["boolean"],
};

const STANDARD_TYPE_COMPAT = { UserProfile: ["User"] };

const LABEL_SOURCE_URI = "URL variable";
const LABEL_SOURCE_CONSTANT = "Constant";
// "Context" = whole in-context entity (contextType); "Context Field" = a field
// walked off an in-context entity (context). Same label on every surface.
const LABEL_SOURCE_CONTEXT = "Context";
const LABEL_SOURCE_CONTEXT_FIELD = "Context Field";
const LABEL_SOURCE_SESSION = "Session";
const LABEL_SOURCE_CONFIG = "Configuration (legacy)";
const LABEL_SOURCE_FEED_ITEM = "Feed Item";

// Gate on the bare type so a "Story" candidate satisfies a seeded "LegacyStory"
// pin (or vice versa) — the #39/#107 spelling class.
function bareEntityType(value) {
  return typeof value === "string" ? value.replace(/^Legacy/, "") : value;
}
function sameBareType(left, right) {
  return bareEntityType(left) === bareEntityType(right);
}

const NOT_DEPLOYED_SUFFIX = " · Not deployed yet";

class ContextSourceOptions {

  /** ******************* Public Static Methods *********************/

  // Prefer the owning app's declaration: the flat entityType-keyed map is
  // last-writer-wins when apps share a key (e.g. every app's "Configuration").
  static resolveEntityConfig(entityType, appName, configsByApp, flat) {
    const lookup = key => {
      const scoped = appName ? configsByApp?.[appName]?.entities?.[key] : null;
      return scoped ?? flat?.[key] ?? null;
    };
    // Seeded contexts persist "Legacy<Type>"; the defs are keyed by the bare
    // type. Without this fallback a seeded context resolves no fields at all.
    const bare = typeof entityType === "string" ? entityType.replace(/^Legacy/, "") : entityType;
    return lookup(entityType) ?? (bare === entityType ? null : lookup(bare));
  }

  // Exposed so consumers (e.g. ProvideContextSourceFields's chain-field gate) match
  // types the same Legacy-agnostic way this builder does.
  static sameBareType(left, right) {
    return bareEntityType(left) === bareEntityType(right);
  }

  static contextTypeBindingIsComplete(candidate, pinnedEntityType) {
    return Boolean(candidate) && (!pinnedEntityType || this.sameBareType(candidate.entityType, pinnedEntityType));
  }

  static resolveAppTitle(appName, configsByApp) {
    if (!appName) {
      return null;
    }
    return configsByApp?.[appName]?.title || appName;
  }

  // Source options. ONE adaptive "Context" source: bind an existing context of
  // the same type directly, or (when it's a different type) walk a oneEntity
  // relationship to the target — the field picker appears only when needed.
  // Pinned mode grays Context when no candidate can source the type either way.
  // URI always appears but is disabled (grayed) when the page has no uri vars —
  // consistent placement rather than a source that vanishes.
  // XXX: `feedItemCandidate` is its OWN input, supplied only when the element is a
  // XXX: feed. It is NOT a member of contextCandidates -- the feed's iterated row
  // XXX: is a separate source, never a row inside the Context list. Smuggling it
  // XXX: into the candidate list is what forced every consumer to filter it back
  // XXX: out again.
  static buildSourceOptions({
    allowedSources,
    uriVarCount,
    includeLegacyConfig,
    pinnedEntityType,
    contextCandidates,
    feedItemCandidate,
    sessionCandidates,
    savedSource = null,
  }) {
    const sources = savedSource && !allowedSources.includes(savedSource)
      ? [...allowedSources, savedSource]
      : allowedSources;
    const options = [];
    if (sources.includes(KEY_SOURCE_URI)) {
      options.push({ value: KEY_SOURCE_URI, label: LABEL_SOURCE_URI, disabled: uriVarCount === 0 });
    }
    if (sources.includes(KEY_SOURCE_CONSTANT)) {
      options.push({ value: KEY_SOURCE_CONSTANT, label: LABEL_SOURCE_CONSTANT, disabled: false });
    }
    if (feedItemCandidate) {
      const rowMatches = !pinnedEntityType || sameBareType(feedItemCandidate.entityType, pinnedEntityType);
      const feedOption = {
        value: KEY_SOURCE_FEED_ITEM,
        label: `${LABEL_SOURCE_FEED_ITEM} (${feedItemCandidate.entityType})`,
        disabled: !rowMatches,
      };
      // A greyed source with no explanation is undiagnosable.
      if (!rowMatches) {
        feedOption.disabledReason = MSG_WRONG_TYPE
            .replace("%source", `${LABEL_SOURCE_FEED_ITEM} (${feedItemCandidate.entityType})`)
            .replace("%have", feedItemCandidate.entityType)
            .replace("%want", pinnedEntityType);
      }
      options.push(feedOption);
    }
    if (sources.includes(KEY_SOURCE_CONTEXT_TYPE)) {
      const hasDirectMatch = contextCandidates.some(candidate => !pinnedEntityType
        || sameBareType(candidate.entityType, pinnedEntityType));
      options.push({
        value: KEY_SOURCE_CONTEXT_TYPE,
        label: LABEL_SOURCE_CONTEXT,
        disabled: !hasDirectMatch,
      });
    }
    if (sources.includes(KEY_SOURCE_CONTEXT)) {
      // Enabled whenever there is anything to walk FROM. Whether any field
      // actually reaches the target is answered by the field dropdown, which
      // shows every relationship and disables the ones that miss.
      options.push({
        value: KEY_SOURCE_CONTEXT,
        label: LABEL_SOURCE_CONTEXT_FIELD,
        disabled: contextCandidates.length === 0,
      });
    }
    if (sources.includes(KEY_SOURCE_SESSION)) {
      const rows = ContextSourceOptions.buildStandardOptions({
        standardRows: sessionCandidates ?? [],
        pinnedEntityType,
      });
      options.push({
        value: KEY_SOURCE_SESSION,
        label: LABEL_SOURCE_SESSION,
        disabled: rows.every(row => row.disabled) || rows.length === 0,
      });
    }
    // The config source is legacy: editable in place so old entries stay
    // maintainable, never offered for new entries.
    if (includeLegacyConfig) {
      options.push({ value: KEY_SOURCE_CONFIG, label: LABEL_SOURCE_CONFIG, disabled: false });
    }
    return options;
  }

  // Standard-entity rows already carry label/secondaryLabel (see
  // StandardContextEntities.enumerate) — this only applies the type gate.
  // XXX: sameBareType, NOT ===. Required contexts are stored Legacy-prefixed
  // XXX: (LegacyStory, LegacyUser) while a standard row's entityType is bare, so
  // XXX: an === compare greyed out "Current User" for every Legacy-spelled pin
  // XXX: and the author read the disabled row as missing.
  static buildStandardOptions({ standardRows, pinnedEntityType }) {
    const compatibleTypes = STANDARD_TYPE_COMPAT[bareEntityType(pinnedEntityType)] ?? [];
    return standardRows.map(row => ({
      ...row,
      disabled: Boolean(pinnedEntityType)
        && !ContextSourceOptions.sameBareType(row.entityType, pinnedEntityType)
        && !compatibleTypes.some(type => ContextSourceOptions.sameBareType(type, row.entityType)),
    }));
  }

  // XXX: THE Context source list. This is the whole algorithm: take the DC's
  // XXX: declared contexts (helpers/dc-context-candidates.js declaredContextList
  // XXX: -- the ONE lookup), show each one's ALIAS and its TYPE, and disable the
  // XXX: ones whose type cannot satisfy the target. No filtering, no merging, no
  // XXX: ambient rows, no feed row, no app-title derivation, no relationship walk.
  // XXX: If you are about to drop a row here, disable it with a reason instead --
  // XXX: a missing row is indistinguishable from a bug.
  static buildDeclaredContextOptions({ candidates, pinnedEntityType }) {
    return candidates.map(candidate => {
      const have = candidate.entityType || candidate.valueType || "";
      const matches = !pinnedEntityType || sameBareType(candidate.entityType, pinnedEntityType);
      const option = {
        value: candidate.alias,
        label: candidate.alias,
        secondaryLabel: have || "no type",
        disabled: !matches,
      };
      if (!matches) {
        option.disabledReason = MSG_WRONG_TYPE
            .replace("%source", candidate.alias)
            .replace("%have", have || "value with no type")
            .replace("%want", pinnedEntityType);
      }
      return option;
    });
  }

  // The Context FIELD source's ENTITY list: the SAME list, the SAME display, and
  // NOTHING disabled. Which context can reach the target is not a property of the
  // context -- it is a property of its FIELDS, so the gate belongs one dropdown
  // later, in buildChainFieldOptions. Disabling a root here hid entities whose
  // relationships were perfectly capable of reaching the target.
  static buildContextFieldOptions({ candidates }) {
    return candidates.map(candidate => ({
      value: ContextSourceOptions.requireAlias(candidate, "buildContextFieldOptions"),
      label: candidate.alias,
      secondaryLabel: candidate.entityType || candidate.valueType || "no type",
      disabled: false,
    }));
  }

  // XXX: A candidate with no `alias` is BROKEN INPUT, not an empty row. The page
  // XXX: and component provides surfaces used to hand-roll their candidate lists
  // XXX: keyed `name`; every option then rendered { value: undefined, label:
  // XXX: undefined } and the picker showed "No results found" with a context
  // XXX: plainly in scope. It took a live repro to find because nothing said a
  // XXX: word. Candidates come from declaredContextList -- THE one lookup -- and
  // XXX: its rows always carry an alias.
  static requireAlias(candidate, caller) {
    if (!candidate?.alias) {
      // eslint-disable-next-line no-console
      console.error(
        `${caller}: a context candidate arrived with no "alias" `
        + `(keys: ${Object.keys(candidate ?? {}).join(", ") || "none"}). `
        + "Candidate rows must come from declaredContextList, which emits "
        + "{ alias, entityType, valueType, appName }. A row keyed \"name\" renders "
        + "as a blank, unselectable option.",
      );
    }
    return candidate?.alias;
  }

  // The second dropdown for source=context_field. ONLY oneEntity fields (a scalar
  // cannot carry an entity context), every one of them DISPLAYED, and the ones
  // pointing at the wrong entity type DISABLED with a reason. Bare-vs-Legacy
  // spellings compare equal -- an exact compare here is the #39/#107 bug class.
  static buildChainFieldOptions({ candidate, pinnedEntityType, configsByApp, flat }) {
    const sourceEntityConfig = ContextSourceOptions.resolveEntityConfig(
      candidate?.entityType, candidate?.appName, configsByApp, flat,
    );
    return Object.entries(sourceEntityConfig?.fields ?? {})
        .filter(([, fieldDef]) => fieldDef?.type === "oneEntity")
        .map(([fieldName, fieldDef]) => {
          const matches = !pinnedEntityType || sameBareType(fieldDef.entity, pinnedEntityType);
          const option = {
            value: fieldName,
            label: fieldName,
            secondaryLabel: fieldDef.entity,
            disabled: !matches,
          };
          if (!matches) {
            option.disabledReason = MSG_FIELD_WRONG_TYPE
                .replace("%field", fieldName)
                .replace("%have", fieldDef.entity)
                .replace("%want", pinnedEntityType);
          }
          return option;
        });
  }

  // SCALAR fields of the chosen source context's entity that can satisfy a
  // primitive required context. The counterpart of buildChainFieldOptions,
  // which lists the RELATIONSHIP fields for an entity-typed context.
  //
  // Non-matching fields are EXCLUDED, not disabled: for an entity target a
  // disabled row still tells the author "this relationship exists but points
  // elsewhere", which is useful. For a scalar there is nothing to explain — a
  // number field simply cannot satisfy a string context — so listing it only
  // adds noise to what is often a long field list.
  static buildValueFieldOptions({ candidate, valueType, configsByApp, flat }) {
    const allowedFieldTypes = VALUE_TYPE_FIELD_TYPES[valueType];
    if (!allowedFieldTypes) {
      return [];
    }
    const sourceEntityConfig = ContextSourceOptions.resolveEntityConfig(
      candidate?.entityType, candidate?.appName, configsByApp, flat,
    );
    return Object.entries(sourceEntityConfig?.fields ?? {})
        .filter(([fieldName, fieldDef]) => !AppPaletteFieldPolicy.HIDDEN_FIELDS.includes(fieldName)
          && allowedFieldTypes.includes(fieldDef?.type))
        .map(([fieldName, fieldDef]) => ({
          value: fieldName,
          label: fieldName,
          // The concrete field type, so "email" / "enum" reads as deliberate
          // rather than looking like a mis-listed string.
          secondaryLabel: fieldDef.type,
        }));
  }

  // Whether a candidate exposes ANY field that could satisfy `valueType`. Lets
  // the picker gray a context up front instead of offering it and then showing
  // an empty field list.
  static candidateHasValueField({ candidate, valueType, configsByApp, flat }) {
    return ContextSourceOptions.buildValueFieldOptions({
      candidate, valueType, configsByApp, flat,
    }).length > 0;
  }

  // Entity-type picker (constant/uri sources, add/edit mode only — pinned
  // mode locks the type). One row per flat entity key. App entities are
  // titled by owner app ("<App> - <Entity>"); legacy context entities list
  // flat by their plain type name (no "Legacy - " prefix).
  static buildEntityTypeOptions({ flat, configsByApp }) {
    return Object.entries(flat)
        .filter(([entityType, entityConfig]) => !AppPaletteFieldPolicy.isHiddenContextEntity(
          entityConfig?.appName,
          entityConfig?.entityType ?? entityType,
        ))
        .map(([entityType, entityConfig]) => {
          // Built-in entities (legacy/platform) carry an INTERNAL appName that
          // routes element reads ("CreatorStudio") — it must never surface as an
          // app qualifier, so they list under their display name alone. Only a
          // genuine dependency app's entities get the "<App> - <Entity>" form.
          const isBuiltIn = entityConfig?.isLegacyEntity || entityConfig?.isPlatformEntity;
          const title = isBuiltIn
            ? null
            : ContextSourceOptions.resolveAppTitle(entityConfig?.appName, configsByApp);
          const name = entityConfig?.displayName || entityType;
          const label = title ? `${title} - ${name}` : name;
          // A destination app's entity not deployed yet (task 135): pickable, but it renders empty until deploy.
          return {
            value: entityType,
            label: entityConfig?.draft ? `${label}${NOT_DEPLOYED_SUFFIX}` : label,
          };
        });
  }

  // A "context" entry with a sourceField is a field walk ("Context Field");
  // without one it's a whole-entity bind ("Context").
  static humanContextSourceLabel(entry) {
    const source = entry?.entitySource;
    switch (source) {
      case KEY_SOURCE_CONSTANT: return LABEL_SOURCE_CONSTANT;
      case KEY_SOURCE_URI: return LABEL_SOURCE_URI;
      case KEY_SOURCE_SESSION:
      case "standard": return LABEL_SOURCE_SESSION;
      case KEY_SOURCE_CONFIG:
      case "configuration": return LABEL_SOURCE_CONFIG;
      case KEY_SOURCE_CONTEXT_TYPE: return LABEL_SOURCE_CONTEXT;
      case KEY_SOURCE_CONTEXT:
        return entry?.sourceField ? LABEL_SOURCE_CONTEXT_FIELD : LABEL_SOURCE_CONTEXT;
      default: return source ? String(source) : "";
    }
  }

}


module.exports = ContextSourceOptions;
